import CourseStatusLabel from "@components/CourseStatusLabel";
import Highlighter from "react-highlight-words";
import React, { useCallback } from "react";
import upperFirst from "lodash/upperFirst";
import {
  Badge,
  Box,
  Flex,
  HStack,
  Heading,
  Text,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import { FiFilm } from "@react-icons/all-files/fi/FiFilm";
import { Link as GatsbyLink } from "gatsby";
import { TUTORIAL_IMAGE_BORDER_RADIUS, TutorialImage } from "./TutorialImage";
import { TutorialAvatar } from "./TutorialAvatar";
import { TutorialProgress } from "./TutorialProgress";

type TutorialCardProps = {
  featuredBy?: string;
  tutorial: {
    lessons: ReadonlyArray<{
      tasks: readonly Pick<Queries.Task, "id">[];
      frontmatter: Pick<Queries.MdxFrontmatter, "time" | "videoId"> | null;
    }>;
  } & Pick<
    Queries.Course,
    | "slug"
    | "title"
    | "description"
    | "thumb"
    | "type"
    | "status"
    | "version"
    | "authors"
  >;
  search?: string;
  completedTasks?: string[] | null;
  pageSource: string;
  isBrowseFeatured?: boolean;
};

export function TutorialCard({
  tutorial,
  search,
  completedTasks,
  featuredBy,
  pageSource,
  isBrowseFeatured = false,
}: TutorialCardProps) {
  const bg = useColorModeValue("silver.400", "whiteAlpha.100");
  const bgHover = useColorModeValue("blackAlpha.100", "whiteAlpha.200");
  const yellow = useToken("colors", "yellow.200");

  const { title, description, authors, thumb, type, status, lessons } =
    tutorial;

  // Only return instructor for avatar if we have the specific name & picture of instructor
  const primaryAuthor = authors?.filter(
    (author) => !["Apollo", "Apollo Solutions"].includes(author?.name ?? "")
  )[0];

  const tasks = lessons?.flatMap((lesson) => lesson.tasks || []) ?? [];

  /* Duplicated from CoursePage - can we surface this higher? */
  const hasVideo = lessons.some((lesson) => lesson.frontmatter?.videoId);

  const videoTime = lessons.reduce(
    (acc, lesson) => acc + (lesson.frontmatter?.time ?? 0),
    0
  );

  const renderText = useCallback(
    (text) =>
      search ? (
        <Highlighter
          highlightStyle={{
            background: yellow,
          }}
          textToHighlight={text}
          searchWords={[search]}
        />
      ) : (
        text
      ),
    [search, yellow]
  );

  const featuredStyles = {
    borderWidth: 1,
    borderColor: "orange.400",
    bgGradient: "linear(to-br, silver.400, orange.200)",
    _dark: {
      bgGradient: "linear(to-br, indigo.300, indigo.500)",
      borderColor: "indigo.200",
    },
    _hover: {
      bgGradient: "linear(to-br, silver.300, orange.200)",
      _dark: {
        bgGradient: "linear(to-br, indigo.200, indigo.500)",
      },
    },
  };
  const baseStyles = {
    bg,
    borderWidth: 0,
    borderColor: "transparent",
    _hover: { bg: bgHover },
  };
  const cardStyles = isBrowseFeatured ? featuredStyles : baseStyles;

  return (
    <Flex
      as={GatsbyLink}
      to={`/${tutorial.slug}`}
      direction="column"
      rounded="xl"
      {...cardStyles}
      transitionProperty="common"
      transitionDuration="normal"
      onClick={() => {
        window.gtag?.("event", "tutorial_card_click", {
          course: tutorial.slug,
          pageSource,
        });
      }}
    >
      <Flex grow={1} direction="column" p="6">
        <Box pos="relative">
          {hasVideo && (
            <TutorialAvatar
              name={primaryAuthor?.name ?? undefined}
              image={primaryAuthor?.image ?? undefined}
            />
          )}
          <Box
            pos="relative"
            overflow="hidden"
            rounded={TUTORIAL_IMAGE_BORDER_RADIUS}
            mb="4"
          >
            <TutorialImage rounded="none" {...thumb} />

            {status && (
              <Box pos="absolute" top="0.5" left="0">
                <CourseStatusLabel status={status} />
              </Box>
            )}
          </Box>
        </Box>
        <Heading mb="1" as="h3" size="md">
          <HStack>
            <span>{renderText(title)}</span>
            {tutorial.version && (
              <Badge variant="outline" fontSize="sm">
                {tutorial.version}
              </Badge>
            )}
          </HStack>
        </Heading>

        <Text mb="10">{renderText(description)}</Text>

        <Flex mt="auto" justify="space-between">
          <Box>
            <Box mr="auto" textStyle="smallCaps" alignContent={"flex-start"}>
              {type ? type.split("-").map(upperFirst).join(" ") : "Course"}
            </Box>
            {hasVideo && (
              <Flex gap="2" justify="flex-end" align="center">
                <FiFilm />
                <Text fontSize="sm">{videoTime} min</Text>
              </Flex>
            )}
          </Box>
          {tasks.length > 0 && completedTasks && (
            <TutorialProgress
              tasks={tasks.map((task) => task.id)}
              completedTasks={completedTasks}
            />
          )}
        </Flex>
      </Flex>
      {featuredBy && (
        <Box borderTopWidth={1} px="6" py="4" fontSize="sm">
          ✨ Recommended by <strong>{featuredBy}</strong>
        </Box>
      )}
    </Flex>
  );
}
